import type {
    PrhHolidayHomeOnsaDTO,
    PrhHouseOnsaDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa/house';
import { COVERABLE_CODE } from '../../../../../constants/coverable';

const isConstructionYearValid = (constructionYear: number | undefined): boolean => {
    return (
        constructionYear !== undefined &&
        constructionYear > 999 &&
        constructionYear < new Date().getFullYear() + 2
    );
};

const isLivingAreaValid = (livingArea: number | undefined): boolean => {
    return livingArea !== undefined && livingArea > 0 && livingArea < 10000;
};

const isDevelopedAreaValid = (developedArea: number | undefined): boolean => {
    return developedArea !== undefined && developedArea > 0 && developedArea < 10000;
};

const isBasementAreaValid = (basementArea: number | undefined): boolean => {
    return basementArea !== undefined && basementArea > 0 && basementArea < 10000;
};

const isGarageValid = (garageOuthouseArea: number | undefined): boolean => {
    return (
        garageOuthouseArea !== undefined && garageOuthouseArea >= 0 && garageOuthouseArea < 10000
    );
};

const isNumberOfToiletsValid = (numberOfToilets: number | undefined): boolean => {
    return numberOfToilets !== undefined && numberOfToilets >= 0 && numberOfToilets < 10000;
};

const isFireplaceValid = (fireplace: string | undefined): boolean => {
    return fireplace !== undefined;
};

const isFormValid = (bbrData: PrhHouseOnsaDTO | PrhHolidayHomeOnsaDTO): boolean => {
    if (!isConstructionYearValid(bbrData.constructionYear)) return false;
    if (
        bbrData.flavourCode === COVERABLE_CODE.prhHouse &&
        !isLivingAreaValid((bbrData as PrhHouseOnsaDTO).livingArea)
    ) {
        return false;
    }
    if (!isDevelopedAreaValid(bbrData.developedArea)) return false;
    if (bbrData.numberOfFloors === undefined) return false;
    if (!bbrData.isApartment && bbrData.basement === undefined) return false;
    if (
        bbrData.flavourCode === COVERABLE_CODE.prhHouse &&
        bbrData.basement === 'Ja' &&
        !isBasementAreaValid((bbrData as PrhHouseOnsaDTO).basementArea)
    )
        return false;
    if (!isGarageValid(bbrData.garageOuthouseArea)) return false;
    if (bbrData.roofType === undefined) return false;
    if (!bbrData.isApartment && bbrData.exteriorWallType === undefined) return false;
    if (!isNumberOfToiletsValid(bbrData.numberOfToilets)) return false;
    if (bbrData.primaryHeatingType === undefined) return false;
    if (
        bbrData.flavourCode === COVERABLE_CODE.prhHolidayHome &&
        !isFireplaceValid((bbrData as PrhHolidayHomeOnsaDTO).fireplace)
    ) {
        return false;
    }
    if (
        bbrData.flavourCode === COVERABLE_CODE.prhHouse &&
        (bbrData as PrhHouseOnsaDTO).commercialActivitiesInTheBuilding === undefined
    )
        return false;
    if (bbrData.closedDownFarm === undefined) return false;
    if (bbrData.closedDownCommercial === undefined) return false;
    return true;
};

const shouldShowCallMe = (buildingData: PrhHouseOnsaDTO | PrhHolidayHomeOnsaDTO): boolean => {
    if (
        buildingData.flavourCode === COVERABLE_CODE.prhHolidayHome &&
        (buildingData as PrhHolidayHomeOnsaDTO).buildingType !== 'HolidayHomeType' &&
        (buildingData as PrhHolidayHomeOnsaDTO).buildingType !== 'AllYearType'
    ) {
        return true;
    }
    if (
        buildingData.flavourCode === COVERABLE_CODE.prhHouse &&
        (buildingData as PrhHouseOnsaDTO).commercialActivitiesInTheBuildingOptions?.find(
            (option) =>
                option.displayName ===
                (buildingData as PrhHouseOnsaDTO).commercialActivitiesInTheBuilding
        )?.code !== 'No'
    )
        return true;
    if (
        buildingData.listedWorthPreservingTypeOptions?.find(
            (option) => option.displayName === buildingData.listedWorthPreservingType
        )?.code !== 'No'
    )
        return true;
    if (buildingData.closedDownCommercial !== 'Nej') return true;
    if (buildingData.closedDownFarm !== 'Nej') return true;
    const roofTypeCode = buildingData.roofTypeOptions?.find(
        (option) => option.displayName === buildingData.roofType
    )?.code;
    if (roofTypeCode === 'StrawWithprotectionRoof' || roofTypeCode === 'StrawWithoutProtectionRoof')
        return true;
    const exteriorWallCode = buildingData.exteriorWallTypeOptions?.find(
        (option) => option.displayName === buildingData.exteriorWallType
    )?.code;
    if (
        exteriorWallCode === 'plasteredWood' ||
        exteriorWallCode === 'logs' ||
        exteriorWallCode === 'otherMaterials'
    )
        return true;
    if (buildingData.waterProtectionType !== 'Nej') return true;
    if (
        buildingData.primaryHeatingTypeOptions?.find(
            (option) => option.displayName === buildingData.primaryHeatingType
        )?.code === 'OtherHeating'
    )
        return true;
    return false;
};

const shouldShowRemoveAndContinue = (
    buildingData: PrhHouseOnsaDTO | PrhHolidayHomeOnsaDTO
): boolean => {
    if (
        buildingData.primaryHeatingTypeOptions?.find(
            (option) => option.displayName === buildingData.primaryHeatingType
        )?.code === 'StrawHeating'
    )
        return true;
    if (
        buildingData.exteriorWallTypeOptions?.find(
            (option) => option.displayName === buildingData.exteriorWallType
        )?.code === 'strawFlaxSeaweed'
    )
        return true;
    if (buildingData.addressIsBornholm) return true;
    if (
        buildingData.numberOfFloorsOptions?.find(
            (option) => option.displayName === buildingData.numberOfFloors
        )?.code === '4OrMore'
    )
        return true;
    return false;
};

export {
    isConstructionYearValid,
    isLivingAreaValid,
    isDevelopedAreaValid,
    isBasementAreaValid,
    isGarageValid,
    isNumberOfToiletsValid,
    isFireplaceValid,
    isFormValid,
    shouldShowRemoveAndContinue,
    shouldShowCallMe
};
